import { ref } from 'vue'
import { useQueryClient } from '@tanstack/vue-query'
import { useRouter } from 'vue-router'
import store from '@/store'
import { postV2Cart, toPostShape } from '@/composables/useCartMutation'
import loadV2Selection from '@/utils/loadV2Selection'

const resolve = (value) => (typeof value === 'function' ? value() : value?.value ?? value)

export async function fetchBrowseMenu(storeId) {
  const token = await store.dispatch('getToken', {
    subdomain: window.location.host
  })

  const res = await fetch(`${process.env.VUE_APP_API_BASE_URL}/merchant/v2/store/${storeId}/menu`, {
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: 'Bearer ' + token
    }
  })

  return res.ok ? res.json() : null
}

export function canBrowseMenu(menu) {
  if (!menu?.store) return false
  const asksBeforeSelection = (menu.store.questions || []).some((q) => q.belongs_to_setting === 'before_selection')
  return !asksBeforeSelection && (menu.store.packages || []).length === 0
}

export function useStartCart(storeId, menu) {
  const queryClient = useQueryClient()
  const router = useRouter()
  const starting = ref(false)
  const failed = ref(false)
  const items = ref([])

  function remember(item) {
    const others = items.value.filter((known) => known.frontend_key !== item?.frontend_key)
    items.value = item?.quantity > 0 ? [...others, item] : others
  }

  async function startCart(item) {
    remember(item)
    if (starting.value || items.value.length === 0) return
    starting.value = true
    failed.value = false
    try {
      const bottle = await store.dispatch('getNewBottleId', resolve(storeId))
      const cart = await postEverything(bottle.id)
      if (!cart) return
      queryClient.setQueryData(['v2-store', bottle.id], resolve(menu))
      queryClient.setQueryData(['v2-cart', bottle.id], cart)
      await openCart(bottle.id)
    } catch (e) {
      items.value = []
      failed.value = true
    } finally {
      starting.value = false
    }
  }

  async function postEverything(bottleId) {
    let cart = null
    let posted = null
    while (posted !== items.value) {
      posted = items.value
      if (posted.length === 0) return null
      cart = await postV2Cart(bottleId, { cart_items: toPostShape(posted), answers: [] })
    }
    return cart
  }

  async function openCart(bottleId) {
    await loadV2Selection().catch(() => null)
    store.commit('SET_CART_HANDOFF_SCROLL', window.scrollY)
    try {
      const navigationFailure = await router.replace({ name: 'Selection', params: { bottleid: bottleId } })
      if (navigationFailure) throw navigationFailure
    } catch (error) {
      store.commit('SET_CART_HANDOFF_SCROLL', null)
      throw error
    }
  }

  return { starting, failed, items, startCart }
}
